import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "./theme";

const navItems = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category" },
  { label: "排行榜", to: "/rank" },
  { label: "关于我们", to: "/about" },
  { label: "常见问题", to: "/faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/category?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="font-heading text-2xl leading-none text-primary-700 dark:text-primary-400"
            aria-label="策驰漫画网首页"
          >
            策驰漫画网
          </Link>
          <div className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  location.pathname === item.to
                    ? "bg-primary-600 text-background-50"
                    : "text-foreground-700 hover:bg-background-200/70 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={onSearch} className="hidden items-center lg:flex">
            <div className="flex items-center rounded-full border border-background-300/70 bg-background-50 px-3 py-1.5 focus-within:border-primary-400">
              <i className="ri-search-line mr-2 text-base text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画"
                aria-label="搜索漫画"
                className="w-36 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
          <button
            type="button"
            onClick={toggle}
            aria-label={isDark ? "切换到日间模式" : "切换到夜间模式"}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300/70 bg-background-50 text-foreground-700 hover:text-accent-500"
          >
            <i className={`text-lg ${isDark ? "ri-sun-line" : "ri-moon-line"}`}></i>
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300/70 bg-background-50 text-foreground-700 md:hidden"
          >
            <i className="text-lg ri-menu-line"></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="border-t border-background-200/70 bg-background-50 px-4 pb-4 md:hidden">
          <div className="flex flex-col gap-1 pt-3">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-lg px-4 py-2.5 text-sm ${
                  location.pathname === item.to
                    ? "bg-primary-600 text-background-50"
                    : "text-foreground-800 hover:bg-background-200/70"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <form onSubmit={onSearch} className="mt-3">
            <div className="flex items-center rounded-full border border-background-300/70 px-3 py-2">
              <i className="ri-search-line mr-2 text-base text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画"
                aria-label="搜索漫画"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
          </form>
        </div>
      )}
    </header>
  );
}